export type PushNotificationType =
  | 'transmision'
  | 'programa'
  | 'locutor'
  | 'evento'
  | 'concurso'
  | 'promocion'
  | 'patrocinador';

export interface PushNotificationTypeMeta {
  type: PushNotificationType;
  label: string;
  icon: string;
  description: string;
  color: string;
  bgColor: string;
  badgeColor: string;
}

export interface PushSendRecord {
  id: number;
  type: PushNotificationType;
  title: string;
  message: string;
  sentAt: string | null; // null = programado, string = enviado
  scheduledFor: string | null;
  recipients: number;
  delivered: number;
  opened: number;
  status: 'enviado' | 'programado' | 'enviando' | 'fallido';
  targetHost?: string;
  targetProgram?: string;
}

export const pushNotificationTypes: PushNotificationTypeMeta[] = [
  {
    type: 'transmision',
    label: 'Inicio transmisión',
    icon: 'ri-broadcast-line',
    description: 'Notificá a tus oyentes cuando iniciás transmisión en vivo. Ideal para que no se pierdan ningún momento.',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    badgeColor: 'bg-red-500',
  },
  {
    type: 'programa',
    label: 'Inicio programa',
    icon: 'ri-calendar-check-line',
    description: 'Avisá cuando arranca un programa específico. Los oyentes reciben una alerta con el nombre y horario.',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    badgeColor: 'bg-amber-500',
  },
  {
    type: 'locutor',
    label: 'Locutor en vivo',
    icon: 'ri-mic-line',
    description: 'Notificá cuando un locutor sale al aire. Tus oyentes se conectan apenas escuchan su nombre.',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
    badgeColor: 'bg-emerald-500',
  },
  {
    type: 'evento',
    label: 'Evento',
    icon: 'ri-calendar-event-line',
    description: 'Promocioná eventos especiales, maratones musicales, sesiones en vivo o transmisiones desde exteriores.',
    color: 'text-rose-600',
    bgColor: 'bg-rose-100',
    badgeColor: 'bg-rose-500',
  },
  {
    type: 'concurso',
    label: 'Concurso',
    icon: 'ri-trophy-line',
    description: 'Anunciá concursos y sorteos. La notificación incluye instrucciones para participar y el premio.',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
    badgeColor: 'bg-indigo-500',
  },
  {
    type: 'promocion',
    label: 'Promoción',
    icon: 'ri-fire-line',
    description: 'Lanzá promociones especiales, descuentos de patrocinadores o contenido exclusivo por tiempo limitado.',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
    badgeColor: 'bg-cyan-500',
  },
  {
    type: 'patrocinador',
    label: 'Patrocinador',
    icon: 'ri-hand-heart-line',
    description: 'Enviá mensajes patrocinados a tu audiencia. Ideal para campañas publicitarias de marcas aliadas.',
    color: 'text-violet-600',
    bgColor: 'bg-violet-100',
    badgeColor: 'bg-violet-500',
  },
];

export interface PlanConsumption {
  planTotal: number;
  usedThisMonth: number;
  remaining: number;
  planName: string;
  resetsAt: string;
}

export const planConsumption: PlanConsumption = {
  planTotal: 200,
  usedThisMonth: 87,
  remaining: 113,
  planName: 'Pro',
  resetsAt: '2026-07-01T00:00:00Z',
};

const now = new Date();
const hoursAgo = (h: number) => new Date(now.getTime() - h * 3600000).toISOString();
const hoursFromNow = (h: number) => new Date(now.getTime() + h * 3600000).toISOString();

export const pushSendHistory: PushSendRecord[] = [
  {
    id: 1,
    type: 'transmision',
    title: '¡Estamos al aire!',
    message: 'RadioFrecuenciaWeb inició transmisión. Conectate ahora y disfrutá de la mejor música en vivo. ¡No te lo pierdas!',
    sentAt: hoursAgo(2),
    scheduledFor: null,
    recipients: 1247,
    delivered: 1203,
    opened: 487,
    status: 'enviado',
  },
  {
    id: 2,
    type: 'programa',
    title: 'Arranca El Mañanero',
    message: 'Valentina Ríos inicia El Mañanero con las mejores noticias, entrevistas y la música que te despierta. Sintonizá ahora.',
    sentAt: hoursAgo(6),
    scheduledFor: null,
    recipients: 1247,
    delivered: 1189,
    opened: 523,
    status: 'enviado',
    targetHost: 'Valentina Ríos',
    targetProgram: 'El Mañanero',
  },
  {
    id: 3,
    type: 'evento',
    title: 'Maratón Musical 80s esta noche',
    message: 'Preparate para 4 horas de los mejores clásicos de los 80. Hoy a las 20:00. ¡Invitá a tus amigos y armá la previa!',
    sentAt: hoursAgo(24),
    scheduledFor: null,
    recipients: 1247,
    delivered: 1156,
    opened: 612,
    status: 'enviado',
  },
  {
    id: 4,
    type: 'concurso',
    title: 'Ganá entradas VIP — Último día',
    message: 'Hoy cierran las participaciones para ganar entradas VIP al concierto. Solo necesitás estar escuchando y mandar tu palabra clave.',
    sentAt: hoursAgo(30),
    scheduledFor: null,
    recipients: 1247,
    delivered: 1140,
    opened: 701,
    status: 'enviado',
  },
  {
    id: 5,
    type: 'promocion',
    title: '2x1 en la tienda oficial',
    message: 'Solo por hoy: llevate 2 productos de la tienda oficial por el precio de 1. Usá el código RADIO2X1 al finalizar tu compra.',
    sentAt: hoursAgo(48),
    scheduledFor: null,
    recipients: 1247,
    delivered: 1123,
    opened: 398,
    status: 'enviado',
  },
  {
    id: 6,
    type: 'programa',
    title: 'Zona Deportiva en 30 minutos',
    message: 'Roberto Aguilar trae todo el análisis deportivo. Fútbol nacional, internacional y la previa del partido de esta noche.',
    sentAt: null,
    scheduledFor: hoursFromNow(2),
    recipients: 1247,
    delivered: 0,
    opened: 0,
    status: 'programado',
    targetHost: 'Roberto Aguilar',
    targetProgram: 'Zona Deportiva',
  },
  {
    id: 7,
    type: 'locutor',
    title: 'Andrés Castillo en vivo',
    message: 'Andrés Castillo está al aire con La Hora Pico. Humor, actualidad y la mejor compañía para tu tarde. ¡Conectate ya!',
    sentAt: null,
    scheduledFor: hoursFromNow(5),
    recipients: 1247,
    delivered: 0,
    opened: 0,
    status: 'programado',
    targetHost: 'Andrés Castillo',
    targetProgram: 'La Hora Pico',
  },
  {
    id: 8,
    type: 'patrocinador',
    title: 'Café del Sol — Descuento especial',
    message: 'Presentá esta notificación en Café del Sol y obtené un 15% de descuento en tu bebida favorita. Patrocinador oficial de RadioFrecuenciaWeb.',
    sentAt: hoursAgo(12),
    scheduledFor: null,
    recipients: 1247,
    delivered: 1198,
    opened: 445,
    status: 'enviado',
  },
  {
    id: 9,
    type: 'transmision',
    title: '¡Volvimos al aire!',
    message: 'Transmisión restablecida. Disculpá la interrupción, ya estamos de vuelta con toda la energía. Gracias por tu paciencia.',
    sentAt: hoursAgo(15),
    scheduledFor: null,
    recipients: 1247,
    delivered: 1102,
    opened: 634,
    status: 'enviado',
  },
];